import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Key, RefreshCcw, Edit3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const randomDigits = (len) => {
  const buf = crypto.getRandomValues(new Uint8Array(len));
  return Array.from(buf, (b) => (b % 10).toString()).join("");
};

// ---------------- Rotate admin PIN modal ----------------
const RotateAdminPinModal = ({ open, onClose }) => {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPin("");
      setSubmitting(false);
    }
  }, [open]);

  const valid = /^\d{6}$/.test(pin);
  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.post("/admin/admin/rotate-pin", { new_pin: pin });
      toast.success(`Admin PIN updated. Use ${pin} next sign-in.`);
      onClose();
    } catch {
      toast.error("Couldn't rotate PIN.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="rotate-admin-pin-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Rotate admin PIN
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            New 6-digit PIN. You'll use it the next time you sign in at /staff/login → Manager / Owner.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              data-testid="rotate-admin-pin-input"
              type="text"
              inputMode="numeric"
              value={pin}
              maxLength={6}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              placeholder="000000"
              className="flex-1 bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-3 font-mono text-[26px] tracking-[0.4em] text-napoli-espresso text-center focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
            <button
              type="button"
              data-testid="rotate-admin-pin-random"
              onClick={() => setPin(randomDigits(6))}
              className="px-3 py-3 border border-napoli-gold/40 bg-napoli-cream-warm text-napoli-espresso font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:border-napoli-oxblood/40 inline-flex items-center"
            >
              <RefreshCcw size={12} />
            </button>
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              data-testid="rotate-admin-pin-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="rotate-admin-pin-submit"
              disabled={!valid || submitting}
              onClick={submit}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : "Rotate"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Restaurant info edit modal ----------------
const RestaurantInfoModal = ({ open, onClose, current, onSaved }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        name: current?.name || "",
        tagline: current?.tagline || "",
        established_year: current?.established_year || "",
        hero_image_url: current?.hero_image_url || "",
        address: current?.address || "",
        phone: current?.phone || "",
        hours: current?.hours || "",
      });
      setSubmitting(false);
    }
  }, [open, current]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = { ...form };
      if (body.established_year) body.established_year = Number(body.established_year);
      else delete body.established_year;
      const { data } = await api.patch("/admin/settings/restaurant-info", body);
      toast.success("Restaurant info updated.");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Couldn't save.");
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ k, label, type = "text", placeholder }) => (
    <label className="block">
      <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">{label}</span>
      <input
        data-testid={`restaurant-info-${k}`}
        type={type}
        value={form[k] ?? ""}
        onChange={onChange(k)}
        placeholder={placeholder}
        className="mt-1.5 w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood"
      />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="restaurant-info-modal" className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-left">Edit restaurant info</DialogTitle>
          <DialogDescription className="font-body text-[13px] text-left text-napoli-espresso-soft">Live changes propagate to landing, dashboard and footer.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <Field k="name" label="Name" />
          <Field k="tagline" label="Tagline" />
          <Field k="established_year" label="Established year" type="number" placeholder="2019" />
          <Field k="phone" label="Phone" placeholder="(718) 555-0182" />
          <div className="sm:col-span-2"><Field k="hero_image_url" label="Hero image URL (https)" /></div>
          <div className="sm:col-span-2"><Field k="address" label="Address" /></div>
          <div className="sm:col-span-2"><Field k="hours" label="Hours" placeholder="Tue–Sun 5:30pm – 11:00pm" /></div>
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" data-testid="restaurant-info-cancel" onClick={onClose} className="flex-1 border border-napoli-espresso/40 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm">Cancel</button>
          <button type="button" data-testid="restaurant-info-save" disabled={submitting} onClick={submit} className="flex-1 bg-napoli-oxblood text-napoli-cream py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Loyalty config edit modal ----------------
const LoyaltyConfigModal = ({ open, onClose, current, onSaved }) => {
  const initThresholds = current?.loyalty_config?.tier_thresholds || {
    ospite: current?.tiers?.ospite?.min_points ?? 0,
    amico: current?.tiers?.amico?.min_points ?? 500,
    famiglia: current?.tiers?.famiglia?.min_points ?? 2000,
    padrone: current?.tiers?.padrone?.min_points ?? 5000,
  };
  const initMults = current?.loyalty_config?.tier_multipliers || {
    ospite: current?.tiers?.ospite?.multiplier ?? 1.0,
    amico: current?.tiers?.amico?.multiplier ?? 1.25,
    famiglia: current?.tiers?.famiglia?.multiplier ?? 1.5,
    padrone: current?.tiers?.padrone?.multiplier ?? 2.0,
  };

  const [ppd, setPpd] = useState(current?.points_per_dollar ?? 1);
  const [wb, setWb] = useState(current?.welcome_bonus ?? 100);
  const [thresholds, setThresholds] = useState(initThresholds);
  const [mults, setMults] = useState(initMults);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPpd(current?.points_per_dollar ?? 1);
      setWb(current?.welcome_bonus ?? 100);
      setThresholds(initThresholds);
      setMults(initMults);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        points_per_dollar: Number(ppd),
        welcome_bonus: Number(wb),
        tier_thresholds: {
          ospite: Number(thresholds.ospite),
          amico: Number(thresholds.amico),
          famiglia: Number(thresholds.famiglia),
          padrone: Number(thresholds.padrone),
        },
        tier_multipliers: {
          ospite: Number(mults.ospite),
          amico: Number(mults.amico),
          famiglia: Number(mults.famiglia),
          padrone: Number(mults.padrone),
        },
      };
      const { data } = await api.patch("/admin/settings/loyalty-config", body);
      toast.success("Loyalty config updated. Applies to new transactions only.");
      onSaved?.(data);
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      if (code === "tier_thresholds_not_monotonic") {
        toast.error("Tier thresholds must increase ospite → padrone.");
      } else {
        toast.error("Couldn't save.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const TIERS = ["ospite", "amico", "famiglia", "padrone"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="loyalty-config-modal" className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-left">Edit loyalty configuration</DialogTitle>
          <DialogDescription className="font-body text-[13px] text-left text-napoli-espresso-soft">
            Changes apply to new transactions only. Existing customer balances and tiers are unaffected.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Points per dollar</span>
            <input data-testid="loyalty-ppd" type="number" step="0.1" min="0.1" max="10" value={ppd} onChange={(e) => setPpd(e.target.value)} className="mt-1.5 w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-mono text-[14px] focus:outline-none focus:border-napoli-oxblood" />
          </label>
          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Welcome bonus (pts)</span>
            <input data-testid="loyalty-welcome-bonus" type="number" step="1" min="0" max="1000" value={wb} onChange={(e) => setWb(e.target.value)} className="mt-1.5 w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-mono text-[14px] focus:outline-none focus:border-napoli-oxblood" />
          </label>
          {TIERS.map((t) => (
            <div key={t} className="sm:col-span-2 grid grid-cols-2 gap-3 border-l-2 border-napoli-gold pl-3 py-1">
              <label className="block">
                <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-oxblood">{t} threshold</span>
                <input
                  data-testid={`loyalty-threshold-${t}`}
                  type="number" step="1" min="0"
                  value={thresholds[t] ?? 0}
                  onChange={(e) => setThresholds((p) => ({ ...p, [t]: e.target.value }))}
                  className="mt-1 w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-napoli-oxblood"
                />
              </label>
              <label className="block">
                <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-oxblood">{t} multiplier</span>
                <input
                  data-testid={`loyalty-mult-${t}`}
                  type="number" step="0.05" min="1.0" max="5.0"
                  value={mults[t] ?? 1.0}
                  onChange={(e) => setMults((p) => ({ ...p, [t]: e.target.value }))}
                  className="mt-1 w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-napoli-oxblood"
                />
              </label>
            </div>
          ))}
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" data-testid="loyalty-config-cancel" onClick={onClose} className="flex-1 border border-napoli-espresso/40 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm">Cancel</button>
          <button type="button" data-testid="loyalty-config-save" disabled={submitting} onClick={submit} className="flex-1 bg-napoli-oxblood text-napoli-cream py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Section helper ----------------
const Section = ({ title, sub, children, testId, onEdit, editTestId }) => (
  <section data-testid={testId} className="bg-napoli-marble border border-napoli-gold/20 p-6">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">{title}</p>
        {sub ? <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">{sub}</p> : null}
      </div>
      {onEdit ? (
        <button
          type="button"
          data-testid={editTestId}
          onClick={onEdit}
          className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso px-3 py-1.5 font-body font-semibold text-[10px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm transition-colors whitespace-nowrap"
        >
          <Edit3 size={12} /> Edit
        </button>
      ) : null}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const Kv = ({ k, v, mono = false }) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-napoli-gold/15 last:border-0">
    <span className="font-body font-semibold text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft">{k}</span>
    <span className={`text-napoli-espresso text-right ${mono ? "font-mono" : "font-body"} font-medium text-[14px]`}>{v}</span>
  </div>
);

// ---------------- Page ----------------
const AdminSettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [restaurantOpen, setRestaurantOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch { /* non-critical */ }
  };

  useEffect(() => {
    refresh();
  }, []);

  const tiers = settings?.tiers || {
    ospite: { min_points: 0, multiplier: 1.0 },
    amico: { min_points: 500, multiplier: 1.25 },
    famiglia: { min_points: 2000, multiplier: 1.5 },
    padrone: { min_points: 5000, multiplier: 2.0 },
  };
  const restaurantInfo = settings?.restaurant_info || {
    name: settings?.name || "Napoli",
    tagline: settings?.tagline || "Mangia con noi",
    established_year: 2019,
    address: "",
    phone: "",
    hours: "",
    hero_image_url: "",
  };

  return (
    <section data-testid="admin-settings-page" className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
          Settings
        </p>
        <h1 className="mt-2 font-display font-bold text-[28px] sm:text-[32px] text-napoli-espresso leading-tight">
          Your restaurant configuration.
        </h1>
        <p className="mt-1 font-body italic text-[14px] text-napoli-espresso-soft">
          Restaurant info and loyalty rules are editable. Changes apply to new transactions only.
        </p>
      </div>

      <div className="space-y-5">
        {/* Restaurant info */}
        <Section
          testId="settings-restaurant"
          title="Restaurant info"
          sub="Brand identity strings, address, hours."
          onEdit={() => setRestaurantOpen(true)}
          editTestId="settings-edit-restaurant"
        >
          <Kv k="Name" v={restaurantInfo.name || "Napoli"} />
          <Kv k="Tagline" v={restaurantInfo.tagline || "Mangia con noi"} />
          <Kv k="Established" v={String(restaurantInfo.established_year || 2019)} />
          <Kv k="Address" v={restaurantInfo.address || "—"} />
          <Kv k="Phone" v={restaurantInfo.phone || "—"} />
          <Kv k="Hours" v={restaurantInfo.hours || "—"} />
        </Section>

        {/* Loyalty config */}
        <Section
          testId="settings-loyalty"
          title="Loyalty configuration"
          sub="Point economy & tier thresholds."
          onEdit={() => setLoyaltyOpen(true)}
          editTestId="settings-edit-loyalty"
        >
          <Kv k="Points per dollar" v={Number(settings?.points_per_dollar ?? 1.0).toFixed(2)} mono />
          <Kv k="Welcome bonus" v={`${settings?.welcome_bonus ?? 100} pts`} mono />
          <div className="mt-3 grid grid-cols-2 gap-3">
            {(["ospite", "amico", "famiglia", "padrone"]).map((t) => (
              <div key={t} className="bg-napoli-cream-warm/70 border-l-2 border-napoli-gold px-3 py-2.5">
                <p className="font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-oxblood">{t}</p>
                <p className="mt-1 font-body text-[12px] text-napoli-espresso-soft">
                  <span className="font-mono">{tiers[t]?.min_points}</span> pts · <span className="font-mono">{tiers[t]?.multiplier}×</span>
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* Admin PIN */}
        <Section testId="settings-admin-pin" title="Your admin PIN" sub="Rotate your 6-digit admin sign-in PIN.">
          <button
            type="button"
            data-testid="settings-rotate-admin-pin"
            onClick={() => setRotateOpen(true)}
            className="inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors"
          >
            <Key size={14} /> Rotate admin PIN
          </button>
        </Section>

        {/* Sign out */}
        <Section testId="settings-account" title="Account">
          <div className="bg-napoli-cream-warm/60 border border-napoli-gold/20 p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-body font-semibold text-[13px] text-napoli-espresso">Sign out of admin</p>
              <p className="font-body italic text-[12px] text-napoli-espresso-soft">
                Signed in as {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Owner"}.
              </p>
            </div>
            <button
              type="button"
              data-testid="settings-signout"
              onClick={() => logout("/")}
              className="inline-flex items-center gap-2 border border-napoli-espresso/50 text-napoli-espresso px-4 py-2.5 font-body font-semibold text-[11px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm whitespace-nowrap"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </Section>
      </div>

      <RotateAdminPinModal open={rotateOpen} onClose={() => setRotateOpen(false)} />
      <RestaurantInfoModal open={restaurantOpen} onClose={() => setRestaurantOpen(false)} current={restaurantInfo} onSaved={refresh} />
      <LoyaltyConfigModal open={loyaltyOpen} onClose={() => setLoyaltyOpen(false)} current={settings} onSaved={refresh} />
    </section>
  );
};

export default AdminSettingsPage;
